import {Component, OnDestroy, OnInit} from '@angular/core';
import {HttpClient} from '@angular/common/http';

/** Ritmo de campanha do número da Gestor (estimeRitmo no servidor). */
export interface RitmoDaReativacao {
  intervaloSegundos: number;
  porHora: number;
  porDia: number;
  dias: number;
  janelaInicioHora: number;
  janelaFimHora: number;
  pausado: boolean;
}

/** POST /gestor-ia/leads-site/reativacao/previa */
interface Previa {
  total: number;
  porEtapa: Record<string, number>;
  excluidos: {motivo: string; quantidade: number}[];
  leads: {id: number; nome: string; empresa: string; etapa: string; minutosSemContato: number}[];
  ritmo: RitmoDaReativacao | null;
  extensaoConectada: boolean;
  opcoes: {
    etapas: Record<string, number>; situacoes: Record<string, number>; diasSemContato: Record<string, number>;
    canais: Record<string, number>; origens: Record<string, number>; acessouTeste: Record<string, number>; temComputador: Record<string, number>;
  };
  erroDaMensagem: string | null;
}

export interface LoteDeReativacao {
  id: number;
  total: number;
  estado: 'ATIVA' | 'PAUSADA' | 'CANCELADA' | 'CONCLUIDA';
  criadoEm: string;
  pendentes: number;
  enviados: number;
  pulados: number;
  falhos: number;
  ultimoEnvioEm: string | null;
  /** contados no banco: a tela não converte fuso de data UTC */
  minutosDesdeCriacao: number;
  segundosDesdeUltimoEnvio: number | null;
  mensagemJson: string;
}

interface ItemDoLote { id: number; leadId: number; nome: string; empresa: string; estado: string; motivo: string | null; minutosAtras: number }

/** GET /gestor-ia/leads-site/reativacao */
interface Situacao {
  lote: LoteDeReativacao | null;
  ultimos: ItemDoLote[];
  ritmo: RitmoDaReativacao | null;
  ultimaRodada: {em: string; resultado: string} | null;
  extensaoConectada: boolean;
}

interface Exemplo { leadId: number; nome: string; empresa: string; etapa: string; texto?: string; botoes?: string[]; link?: string | null; erro?: string }

interface Opcao { id: any; nome: string; icone?: string; chave?: string }

export const ETAPAS_DA_REATIVACAO = [
  {id: 'NOVO', nome: 'Novo', cor: '#3b82f6', icone: 'fa-user-plus', mensagem: 'Abordagem fria'},
  {id: 'CONVERSA', nome: 'Em conversa', cor: '#0ea5a4', icone: 'fa-comment', mensagem: 'Retomada com tática'},
  {id: 'TESTE', nome: 'Teste criado', cor: '#8b5cf6', icone: 'fa-flask', mensagem: 'Acesso à loja'},
  {id: 'TESTE_ATIVO', nome: 'Teste ativo', cor: '#6d28d9', icone: 'fa-sign-in-alt', mensagem: 'Retoma de onde parou'},
];

const PROTECOES: Record<string, string> = {
  PEDIU_PARAR: 'Pediu para parar', PRECISA_HUMANO: 'Precisa de humano', CONVERSA_EM_ANDAMENTO: 'Conversando agora',
  FOLLOWUP_AGENDADO: 'Follow-up já agendado', CONTATO_RECENTE: 'Contato há menos de 24 h', JA_NA_FILA: 'Já está em outra reativação',
  ETAPA_CLIENTE: 'Virou cliente', ETAPA_PERDIDO: 'Marcado como perdido', LEAD_REMOVIDO: 'Lead removido',
};

const CANAIS: Record<string, {nome: string; icone: string}> = {
  chatgpt: {nome: 'ChatGPT', icone: 'fa-magic'}, site: {nome: 'Site', icone: 'fa-globe'}, google: {nome: 'Google', icone: 'fa-search'},
  instagram: {nome: 'Instagram', icone: 'fa-camera'}, facebook: {nome: 'Facebook', icone: 'fa-thumbs-up'}, '': {nome: 'Sem canal', icone: 'fa-question'},
};
const ORIGENS: Record<string, {nome: string; icone: string}> = {
  manual: {nome: 'Manual', icone: 'fa-user'}, meucardapio: {nome: 'MeuCardápio', icone: 'fa-store'},
  site_meucardapio: {nome: 'Formulário do site', icone: 'fa-file-alt'}, saudacao_site: {nome: 'Botão do site', icone: 'fa-comment-dots'},
};

/** Por que a fila não andou na última rodada (executeRodadaDeReativacao). */
const MOTIVO_DA_ESPERA: Record<string, string> = {
  FORA_DA_JANELA: 'Fora do horário de envio. A fila recomeça quando a janela abrir.',
  NUMERO_PAUSADO: 'O número da Gestor está pausado na V4. Nada sai até ele ser despausado.',
  EXTENSAO_DESCONECTADA: 'A extensão da Gestor está desconectada. A fila espera e volta sozinha quando ela reconectar.',
  COTA_ESGOTADA: 'O teto de marketing do dia foi atingido. A fila continua amanhã.',
  SEM_NUMERO: 'Nenhum número da extensão é a Mia Gestor.',
  FALHOU: 'O último envio falhou; a fila segue no próximo.',
};

const PASTEIS = [['#fff1e6', '#c2410c'], ['#e6f6f4', '#0f766e'], ['#f1ecfe', '#6d28d9'], ['#e8f0fe', '#1d4ed8'], ['#fdeef3', '#be185d'], ['#ecf8ee', '#15803d']];
const FRASES_DE_IDEIA = [
  {rotulo: 'Condição do mês', texto: 'Fale da condição especial deste mês. Não ofereça desconto além do que está aqui.'},
  {rotulo: 'Novidade no sistema', texto: 'Conte a novidade do sistema que mais ajuda quem ainda não começou a vender pelo MeuCardápio.'},
  {rotulo: 'Só retomar', texto: 'Só retome a conversa de onde parou, com uma pergunta leve. Sem oferta.'},
];

/**
 * Reativação de leads da Mia Gestor: filtros, mensagem (a Mia escreve ou texto fixo), prévia e disparo; depois,
 * o acompanhamento do lote. O envio é do servidor, um lead por vez no ritmo de marketing do número: a tela pode fechar.
 */
@Component({
  selector: 'app-reativacao-leads-mia-gestor',
  templateUrl: './reativacao-leads-mia-gestor.component.html',
  styleUrls: ['./reativacao-leads-mia-gestor.component.scss'],
})
export class ReativacaoLeadsMiaGestorComponent implements OnInit, OnDestroy {
  readonly etapas = ETAPAS_DA_REATIVACAO;
  readonly ideias = FRASES_DE_IDEIA;
  readonly periodos = [{dias: 7, rotulo: '7 dias'}, {dias: 30, rotulo: '30 dias'}, {dias: 90, rotulo: '90 dias'}];
  readonly situacoes: Opcao[] = [
    {id: 'SEM_CONVERSA', nome: 'Nunca escreveu', icone: 'fa-file-alt'},
    {id: 'AGUARDANDO_LEAD', nome: 'Aguardando lead', icone: 'fa-hourglass-half'},
    {id: 'ESFRIOU', nome: 'Esfriou', icone: 'fa-snowflake'},
  ];
  readonly silencios: Opcao[] = [1, 3, 7, 15, 30].map(d => ({id: d, nome: d === 1 ? '1 dia' : `${d} dias`}));
  readonly testes: Opcao[] = [{id: null, nome: 'Qualquer'}, {id: true, nome: 'Entrou', icone: 'fa-sign-in-alt', chave: 'sim'},
    {id: false, nome: 'Não entrou', icone: 'fa-ban', chave: 'nao'}];
  readonly computadores: Opcao[] = [{id: null, nome: 'Qualquer'}, {id: true, nome: 'Tem', icone: 'fa-desktop', chave: 'sim'},
    {id: false, nome: 'Só celular', icone: 'fa-mobile-alt', chave: 'nao'}];

  tela: 'carregando' | 'filtros' | 'andamento' = 'carregando';
  dias = 30;
  etapasEscolhidas = new Set<string>(ETAPAS_DA_REATIVACAO.map(e => e.id));
  situacoesEscolhidas = new Set<string>();
  diasSemContato = 1;
  canais = new Set<string>();
  origens = new Set<string>();
  acessouTeste: boolean | null = null;
  temComputador: boolean | null = null;
  busca = '';

  modo: 'MIA' | 'FIXO' = 'MIA';
  orientacao = '';
  versoes: string[] = ['Oi, {primeiro_nome}! Aqui é a Mia, do MeuCardápio 👋\n\nPassando para lembrar que o teste da *{loja}* continua de pé: 7 dias grátis.\n\nQuer que eu crie agora?', ''];
  versaoAtual = 0;
  botao: 'NENHUM' | 'TESTE' | 'LINK' = 'NENHUM';
  link = '';
  imagem: string | null = null;
  enviandoImagem = false;

  previa: Previa | null = null;
  carregandoPrevia = false;
  exemplos: Exemplo[] = [];
  gerandoExemplos = false;
  mostrarTodos = false;
  disparando = false;
  erro = '';

  situacao: Situacao | null = null;
  agindo = false;
  private consultaDaPrevia: any = null;
  private atualizacao: any = null;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.carregueSituacao(true);
  }

  ngOnDestroy(): void {
    clearTimeout(this.consultaDaPrevia);
    clearInterval(this.atualizacao);
  }

  // ===== filtros e prévia =====

  private filtros() {
    return {
      dias: this.dias, etapas: [...this.etapasEscolhidas], situacoes: [...this.situacoesEscolhidas], diasSemContato: this.diasSemContato,
      canais: [...this.canais], origens: [...this.origens], acessouTeste: this.acessouTeste, temComputador: this.temComputador, busca: this.busca,
    };
  }

  private mensagem() {
    return {modo: this.modo, orientacao: this.orientacao, versoes: this.modo === 'FIXO' ? this.versoes : [], botao: this.botao, link: this.link, imagem: this.imagem};
  }

  /** Recalcula a prévia logo depois da última mudança (busca digitada não dispara uma consulta por tecla). */
  mudou(espera = 250): void {
    clearTimeout(this.consultaDaPrevia);
    this.consultaDaPrevia = setTimeout(() => this.carreguePrevia(), espera);
  }

  carreguePrevia(): void {
    this.carregandoPrevia = true;
    this.http.post<Previa>('/gestor-ia/leads-site/reativacao/previa', {filtros: this.filtros(), mensagem: this.mensagem()}).subscribe({
      next: previa => { this.previa = previa; this.carregandoPrevia = false; this.erro = ''; },
      error: e => { this.carregandoPrevia = false; this.erro = e?.error?.erro || 'Não foi possível calcular a prévia.'; },
    });
  }

  mudePeriodo(dias: number): void { this.dias = dias; this.mudou(0); }

  alterneEtapa(id: string): void {
    if (this.etapasEscolhidas.has(id)) { if (this.etapasEscolhidas.size > 1) this.etapasEscolhidas.delete(id); }
    else this.etapasEscolhidas.add(id);
    this.mudou(0);
  }

  alterne(conjunto: Set<string>, id: string): void {
    if (conjunto.has(id)) conjunto.delete(id); else conjunto.add(id);
    this.mudou(0);
  }

  escolhaSilencio(dias: number): void { this.diasSemContato = dias; this.mudou(0); }
  escolhaTeste(valor: boolean | null): void { this.acessouTeste = valor; this.mudou(0); }
  escolhaComputador(valor: boolean | null): void { this.temComputador = valor; this.mudou(0); }

  contagem(grupo: keyof Previa['opcoes'], chave: any): number | null {
    const g = this.previa?.opcoes?.[grupo];
    return g ? Number(g[String(chave)] || 0) : null;
  }

  get canaisDisponiveis(): Opcao[] {
    return Object.keys(this.previa?.opcoes?.canais || {}).concat([...this.canais])
      .filter((c, i, todos) => todos.indexOf(c) === i)
      .map(c => ({id: c, nome: (CANAIS[c] || {nome: c}).nome, icone: (CANAIS[c] || {icone: 'fa-hashtag'}).icone}));
  }

  get origensDisponiveis(): Opcao[] {
    return Object.keys(this.previa?.opcoes?.origens || {}).concat([...this.origens])
      .filter((o, i, todas) => todas.indexOf(o) === i)
      .map(o => ({id: o, nome: (ORIGENS[o] || {nome: o}).nome, icone: (ORIGENS[o] || {icone: 'fa-tag'}).icone}));
  }

  etapa(id: string) { return ETAPAS_DA_REATIVACAO.find(e => e.id === id) || ETAPAS_DA_REATIVACAO[0]; }

  /** Barra empilhada e legenda: só as etapas com alguém. */
  get porEtapa() {
    const total = this.previa?.total || 0;
    return ETAPAS_DA_REATIVACAO.map(e => ({...e, n: this.previa?.porEtapa?.[e.id] || 0}))
      .map(e => ({...e, pct: total ? e.n / total * 100 : 0}));
  }

  get frios(): number { return this.previa?.porEtapa?.NOVO || 0; }

  get foraDoLote() {
    return (this.previa?.excluidos || []).map(e => ({rotulo: PROTECOES[e.motivo] || e.motivo, quantidade: e.quantidade}));
  }

  get totalForaDoLote(): number { return this.foraDoLote.reduce((t, e) => t + e.quantidade, 0); }

  /** Envios por dia contra o teto diário: hoje e os próximos dias até esvaziar a fila (sempre 4 colunas). */
  get enviosPorDia() {
    const total = this.previa?.total || 0;
    const porDia = Math.max(1, this.previa?.ritmo?.porDia || 100);
    const dias: number[] = [];
    let resta = total;
    while (resta > 0 && dias.length < 5) { dias.push(Math.min(porDia, resta)); resta -= Math.min(porDia, resta); }
    const nomes = ['Hoje', 'Amanhã'];
    const hoje = new Date();
    return [...dias, 0, 0, 0, 0].slice(0, Math.max(4, dias.length)).map((n, i) => {
      const d = new Date(hoje.getTime() + i * 86400000);
      return {n, pct: Math.min(100, n / porDia * 100), nome: nomes[i] || d.toLocaleDateString('pt-BR', {weekday: 'short'}).replace('.', '')};
    });
  }

  get previsao(): string {
    const total = this.previa?.total || 0;
    const ritmo = this.previa?.ritmo;
    if (!total || !ritmo) return '';
    if (ritmo.pausado) return 'O número da Gestor está pausado: a fila só anda quando ele for despausado.';
    const agora = new Date();
    const hora = agora.getHours();
    const dentro = hora >= ritmo.janelaInicioHora && hora < ritmo.janelaFimHora;
    if (ritmo.dias <= 1 && dentro) {
      const fim = new Date(agora.getTime() + (total - 1) * ritmo.intervaloSegundos * 1000);
      if (fim.getHours() < ritmo.janelaFimHora && fim.getDate() === agora.getDate()) {
        return `Termina hoje, por volta das ${fim.toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'})}.`;
      }
    }
    if (!dentro) return `Fora do horário agora: começa às ${ritmo.janelaInicioHora}h e leva ${ritmo.dias} ${ritmo.dias === 1 ? 'dia' : 'dias'} de envio.`;
    return `Termina em ${Math.max(ritmo.dias, 2)} dias de envio, sempre dentro da janela.`;
  }

  get intervaloEmMinutos(): string {
    const s = this.previa?.ritmo?.intervaloSegundos || this.situacao?.ritmo?.intervaloSegundos || 180;
    return s % 60 === 0 ? `${s / 60} min` : `${Math.round(s / 6) / 10} min`;
  }

  get leadsVisiveis() {
    const leads = this.previa?.leads || [];
    return this.mostrarTodos ? leads : leads.slice(0, 8);
  }

  semContato(minutos: number): string {
    const dias = Math.floor(minutos / 1440);
    return dias < 1 ? 'hoje' : dias === 1 ? 'há 1 dia' : `há ${dias} dias`;
  }

  // ===== mensagem =====

  escolhaModo(modo: 'MIA' | 'FIXO'): void { this.modo = modo; this.mudou(0); }

  useIdeia(texto: string): void { this.orientacao = texto; this.exemplos = []; }

  gereExemplos(): void {
    this.gerandoExemplos = true;
    this.exemplos = [];
    this.http.post<{exemplos: Exemplo[]}>('/gestor-ia/leads-site/reativacao/exemplos', {filtros: this.filtros(), mensagem: this.mensagem()}).subscribe({
      next: r => { this.exemplos = r.exemplos || []; this.gerandoExemplos = false; },
      error: e => { this.gerandoExemplos = false; this.erro = e?.error?.erro || 'A Mia não conseguiu montar os exemplos agora.'; },
    });
  }

  get versoesPreenchidas(): number { return this.versoes.filter(v => v.trim()).length; }

  escolhaVersao(i: number): void { this.versaoAtual = i; }

  novaVersao(): void {
    if (this.versoes.length >= 5) return;
    this.versoes.push('');
    this.versaoAtual = this.versoes.length - 1;
  }

  insira(variavel: string, campo: HTMLTextAreaElement): void {
    const ini = campo.selectionStart ?? campo.value.length;
    const fim = campo.selectionEnd ?? ini;
    const texto = this.versoes[this.versaoAtual];
    this.versoes[this.versaoAtual] = texto.slice(0, ini) + variavel + texto.slice(fim);
    setTimeout(() => { campo.focus(); campo.selectionStart = campo.selectionEnd = ini + variavel.length; });
  }

  trackPorIndice(i: number): number { return i; }
  trackPorId(_: number, o: {id: any}): any { return o.id; }

  /** Lead de exemplo do texto fixo: o primeiro da fila. */
  get leadDeExemplo() { return this.previa?.leads?.[0] || null; }

  textoFixoDoExemplo(): string {
    const lead = this.leadDeExemplo;
    const primeiro = String(lead?.nome || '').trim().split(/\s+/)[0] || '';
    const loja = String(lead?.empresa || '').trim() || 'sua loja';
    return String(this.versoes[this.versaoAtual] || '').replace(/\{primeiro_nome\}/g, primeiro).replace(/\{loja\}/g, loja)
      .replace(/,\s*([!?.])/g, '$1').replace(/[ \t]+([!?.,])/g, '$1').trim();
  }

  /** Texto da bolha com *negrito* do WhatsApp, já escapado. */
  bolha(texto: string): string {
    return String(texto || '').replace(/[&<>"]/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;'}[c]))
      .replace(/\*([^*\n]+)\*/g, '<b>$1</b>').replace(/\n/g, '<br>');
  }

  get urlDaImagem(): string { return this.imagem ? `/images/empresa/${this.imagem}` : ''; }

  envieImagem(evento: any): void {
    const arquivo: File = evento?.target?.files?.[0];
    if (!arquivo) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(arquivo.type)) { this.erro = 'Use uma imagem JPG, PNG ou WebP.'; return; }
    if (arquivo.size > 5 * 1024 * 1024) { this.erro = 'A imagem pode ter até 5 MB.'; return; }
    const dados = new FormData();
    dados.append('files', arquivo, arquivo.name);
    this.enviandoImagem = true;
    this.http.post<any>('/upload/imagem', dados).subscribe({
      next: r => { this.imagem = r?.data?.file || null; this.enviandoImagem = false; this.erro = r?.data?.file ? '' : 'Não foi possível subir a imagem.'; },
      error: () => { this.enviandoImagem = false; this.erro = 'Não foi possível subir a imagem.'; },
    });
    evento.target.value = '';
  }

  tireImagem(): void { this.imagem = null; }

  get mensagemPronta(): boolean {
    if (this.modo === 'FIXO' && !this.versoesPreenchidas) return false;
    if (this.modo === 'FIXO' && this.botao === 'LINK' && !/^https:\/\/\S+$/i.test(this.link.trim())) return false;
    return true;
  }

  get resumoDaMensagem(): {icone: string; texto: string}[] {
    const r = this.modo === 'MIA'
      ? [{icone: 'fa-magic', texto: 'A Mia escreve'}, ...(this.orientacao.trim() ? [{icone: 'fa-comment', texto: 'com orientação'}] : [])]
      : [{icone: 'fa-file-alt', texto: `Texto fixo · ${this.versoesPreenchidas} ${this.versoesPreenchidas === 1 ? 'versão' : 'versões'}`},
        ...(this.botao === 'TESTE' ? [{icone: 'fa-gift', texto: 'botão Testar 7 dias'}] : this.botao === 'LINK' ? [{icone: 'fa-link', texto: 'botão de link'}] : [])];
    if (this.imagem) r.push({icone: 'fa-image', texto: 'com imagem'});
    return r;
  }

  // ===== disparo e acompanhamento =====

  dispare(): void {
    if (!this.previa?.total || !this.mensagemPronta || this.disparando) return;
    this.disparando = true;
    this.http.post<{lote: LoteDeReativacao}>('/gestor-ia/leads-site/reativacao', {filtros: this.filtros(), mensagem: this.mensagem()}).subscribe({
      next: () => { this.disparando = false; this.carregueSituacao(false); },
      error: e => { this.disparando = false; this.erro = e?.error?.erro || 'Não foi possível disparar a reativação.'; },
    });
  }

  carregueSituacao(primeira: boolean): void {
    this.http.get<Situacao>('/gestor-ia/leads-site/reativacao').subscribe({
      next: s => {
        this.situacao = s;
        const emAndamento = !!s.lote && (s.lote.estado === 'ATIVA' || s.lote.estado === 'PAUSADA');
        if (emAndamento || (!primeira && s.lote)) this.mostreAndamento();
        else if (primeira) { this.tela = 'filtros'; this.carreguePrevia(); }
      },
      error: e => { this.tela = 'filtros'; this.erro = e?.error?.erro || 'Não foi possível carregar a reativação.'; this.carreguePrevia(); },
    });
  }

  private mostreAndamento(): void {
    this.tela = 'andamento';
    if (!this.atualizacao) this.atualizacao = setInterval(() => this.carregueSituacao(false), 15000);
  }

  acao(acao: 'pausar' | 'retomar' | 'cancelar'): void {
    const lote = this.situacao?.lote;
    if (!lote || this.agindo) return;
    if (acao === 'cancelar' && !confirm(`Cancelar a reativação? Os ${lote.pendentes} leads que ainda não receberam saem da fila.`)) return;
    this.agindo = true;
    this.http.post<{lote: LoteDeReativacao}>(`/gestor-ia/leads-site/reativacao/${lote.id}/${acao}`, {}).subscribe({
      next: () => { this.agindo = false; this.carregueSituacao(false); },
      error: e => { this.agindo = false; this.erro = e?.error?.erro || 'Não foi possível concluir.'; },
    });
  }

  novaReativacao(): void {
    clearInterval(this.atualizacao);
    this.atualizacao = null;
    this.tela = 'filtros';
    this.exemplos = [];
    this.carreguePrevia();
  }

  get lote(): LoteDeReativacao | null { return this.situacao?.lote || null; }
  get loteTerminou(): boolean { return !!this.lote && (this.lote.estado === 'CONCLUIDA' || this.lote.estado === 'CANCELADA'); }

  get progresso() {
    const l = this.lote;
    const total = Math.max(1, Number(l?.total || 0));
    return [
      {cor: '#16a34a', pct: Number(l?.enviados || 0) / total * 100},
      {cor: '#c3c9d4', pct: Number(l?.pulados || 0) / total * 100},
      {cor: '#dc2626', pct: Number(l?.falhos || 0) / total * 100},
    ];
  }

  get selo(): {classe: string; texto: string} {
    const l = this.lote;
    if (!l) return {classe: '', texto: ''};
    if (l.estado === 'CONCLUIDA') return {classe: 'fim', texto: 'Concluída'};
    if (l.estado === 'CANCELADA') return {classe: 'cancelada', texto: 'Cancelada'};
    if (l.estado === 'PAUSADA') return {classe: 'pausada', texto: 'Pausada'};
    return {classe: '', texto: 'Enviando'};
  }

  /** Linha de estado do acompanhamento: o próximo envio ou por que a fila está parada. */
  get proximoEnvio(): string {
    const l = this.lote;
    if (!l) return '';
    if (l.estado === 'CANCELADA') return 'Cancelada. Quem ainda estava na fila não recebe nada.';
    if (l.estado === 'CONCLUIDA') return 'Todos os leads da fila foram processados.';
    if (l.estado === 'PAUSADA') return 'Pausada. Ninguém recebe nada até você retomar.';
    if (this.situacao && !this.situacao.extensaoConectada) return MOTIVO_DA_ESPERA.EXTENSAO_DESCONECTADA;
    const espera = MOTIVO_DA_ESPERA[this.situacao?.ultimaRodada?.resultado || ''];
    if (espera) return espera;
    const intervalo = this.situacao?.ritmo?.intervaloSegundos || 180;
    if (l.segundosDesdeUltimoEnvio === null || l.segundosDesdeUltimoEnvio === undefined) return 'O primeiro envio sai na próxima rodada, em até 1 minuto.';
    const proximo = new Date(Date.now() + Math.max(0, intervalo - Number(l.segundosDesdeUltimoEnvio)) * 1000);
    return `Próximo envio por volta das ${proximo.toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'})}, no intervalo de campanha do número.`;
  }

  detalheDoItem(item: ItemDoLote): string {
    const m = String(item.motivo || '');
    if (item.estado === 'ENVIADO') {
      const fixo = /^TEXTO_FIXO_(\d+)$/.exec(m);
      if (fixo) return `Texto fixo · versão ${fixo[1]}`;
      return ({RETOMADA: 'Retomada com tática', ABERTURA: 'Abordagem fria', ACESSO_A_LOJA: 'Acesso à loja'} as any)[m] || 'Mensagem da Mia';
    }
    return PROTECOES[m] || m || (item.estado === 'FALHOU' ? 'Falhou' : 'Pulado');
  }

  /** "agora", "há 5 min", "há 2 h": contado no banco */
  haQuanto(minutos: number): string {
    const m = Math.max(0, Math.round(Number(minutos) || 0));
    if (m < 1) return 'agora';
    if (m < 60) return `há ${m} min`;
    if (m < 1440) return `há ${Math.floor(m / 60)} h`;
    return `há ${Math.floor(m / 1440)} d`;
  }

  get criadoHa(): string { return this.lote ? this.haQuanto(this.lote.minutosDesdeCriacao) : ''; }

  // ===== aparência =====

  iniciais(nome: string): string {
    return String(nome || '?').trim().split(/\s+/).map(p => p[0]).slice(0, 2).join('').toUpperCase() || '?';
  }

  pastel(id: number): {background: string; color: string} {
    const [fundo, texto] = PASTEIS[Number(id) % PASTEIS.length];
    return {background: fundo, color: texto};
  }
}
